import { WireObject } from 'content-creation-common/models';

import { isNil } from '../../../util/object';

export default class OutputMediaEntityWireObject extends WireObject {
  _outputMediaId = null;
  _outputMediaType = null;
  _adsAPIMediaId = null;
  _rspAssetId = null;
  _mlsMediaId = null;
  _videoBuildingComplete = null;
  _videoBuildingProgress = null;
  _frameStart = null;
  _frameEnd = null;

  /**
   * @param {String|null} outputMediaId - id of the outputMedia
   * @param {String|null} outputMediaType - the type
   * @param {String|null} adsAPIMediaId - ads api media ID that was created from this output
   * @param {String|null} rspAssetId - snapsIds that belong to the edition
   * @param {String|null} mlsMediaId - mlsMediaId MediaLibraryService id
   * @param {Boolean|null} videoBuildingComplete
   * @param {Number|null} videoBuildingProgress
   * @param {Number|null} frameStart
   * @param {Number|null} frameEnd
   */
  constructor(
    outputMediaId = null,
    outputMediaType = null,
    adsAPIMediaId = null,
    rspAssetId = null,
    mlsMediaId = null,
    videoBuildingComplete = null,
    videoBuildingProgress = null,
    frameStart = null,
    frameEnd = null,
  ) {
    super();

    this._outputMediaId = outputMediaId;
    this._outputMediaType = outputMediaType;
    this._adsAPIMediaId = adsAPIMediaId;
    this._rspAssetId = rspAssetId;
    this._mlsMediaId = mlsMediaId;
    this._videoBuildingComplete = videoBuildingComplete;
    this._videoBuildingProgress = videoBuildingProgress;
    this._frameStart = frameStart;
    this._frameEnd = frameEnd;
  }

  /**
   * return {OutputMediaEntity} new OutputMediaEntity without an outputMediaId, based on this one.
   */
  copy() {
    return new OutputMediaEntityWireObject(
      null,
      this.outputMediaType,
      this.adsAPIMediaId,
      this.rspAssetId,
      this.mlsMediaId,
      this.videoBuildingComplete,
      this.videoBuildingProgress,
      this.frameStart,
      this.frameEnd,
    );
  }

  /**
   * Turn CreativeContentItemWireObject in to JSON Object
   * @returns {{width: *, type: *, content: *, height: *}}
   */
  toJSON() {
    return {
      ads_api_media_id: this.adsAPIMediaId,
      frame_end: this.frameEnd,
      frame_start: this.frameStart,
      mls_media_id: this.mlsMediaId,
      output_media_id: this.outputMediaId,
      output_media_type: this.outputMediaType,
      rsp_asset_id: this.rspAssetId,
      video_building_complete: this.videoBuildingComplete,
      video_building_progress: this.videoBuildingProgress,
    };
  }

  get outputMediaId() {
    return this._outputMediaId;
  }

  set outputMediaId(value) {
    this._outputMediaId = value;
  }

  get outputMediaType() {
    return this._outputMediaType;
  }

  set outputMediaType(value) {
    this._outputMediaType = value;
  }

  get adsAPIMediaId() {
    return this._adsAPIMediaId;
  }

  set adsAPIMediaId(value) {
    this._adsAPIMediaId = value;
  }

  get rspAssetId() {
    return this._rspAssetId;
  }

  set rspAssetId(value) {
    this._rspAssetId = value;
  }

  get mlsMediaId() {
    return this._mlsMediaId;
  }

  set mlsMediaId(value) {
    this._mlsMediaId = value;
  }

  get videoBuildingComplete() {
    return this._videoBuildingComplete;
  }

  set videoBuildingComplete(value) {
    this._videoBuildingComplete = value;
  }

  get videoBuildingProgress() {
    return this._videoBuildingProgress;
  }

  set videoBuildingProgress(value) {
    this._videoBuildingProgress = value;
  }

  get frameStart() {
    return this._frameStart;
  }

  set frameStart(value) {
    this._frameStart = value;
  }

  get frameEnd() {
    return this._frameEnd;
  }

  set frameEnd(value) {
    this._frameEnd = value;
  }

  /**
   * Test if incoming is instance of OutputMediaEntityWireObject
   * @param {any} incoming
   * @returns {boolean}
   */
  static isOutputMediaEntityWireObject(incoming) {
    return incoming instanceof OutputMediaEntityWireObject;
  }

  /**
   * Turn JSON in to OutputMediaEntityWireObject
   * @param {Object} inJSON
   * @returns {OutputMediaEntityWireObject}
   */
  static fromJSON(inJSON) {
    if (isNil(inJSON)) {
      throw new Error('OutputMediaEntityWireObject.fromJSON inJSON is nil');
    }

    const {
      ads_api_media_id: adsAPIMediaId,
      frame_end: frameEnd,
      frame_start: frameStart,
      mls_media_id: mlsMediaId,
      output_media_id: outputMediaId,
      output_media_type: outputMediaType,
      rsp_asset_id: rspAssetId,
      video_building_complete: videoBuildingComplete,
      video_building_progress: videoBuildingProgress,
    } = inJSON;

    return new OutputMediaEntityWireObject(
      outputMediaId,
      outputMediaType,
      adsAPIMediaId,
      rspAssetId,
      mlsMediaId,
      videoBuildingComplete,
      videoBuildingProgress,
      frameStart,
      frameEnd,
    );
  }
}
